import { useContext, useEffect, useMemo, useState } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import MetricOptionsContext from '../../../contexts/MetricOptionsContext';
import useFilterInput from '../../../hooks/useFilterInput';
import getPeerComparison from './getPeerComparison';
import isGaugeLowerBetter from './isGaugeLowerBetter';
import useDateScope from '../../../hooks/useDateScope';
import GqlClientContext from '../../../contexts/GqlClientContext';
import useNetworkingEffect from '../../../hooks/useNetworkingEffect';
import metricTypeCheckers from '../../../types/metricTypeCheckers';
import useToMetricInput from '../../../hooks/useToMetricInput';
import useMetricAggregationRequest from '../../../hooks/useMetricAggregationRequest';
import filterTypeCheckers from '../../FilterPlateForm/filterTypeCheckers';
import fiToDrillDowns from '../../../screens/DataManager/Metrics/MetricPopup/MetricForm/NormalMetricForm/fiToDrillDowns';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import { useReportGadgetRequest } from 'contextProviders/GadgetStatusBoundary';
import retryOnce from 'utils/retryOnce';

const usePeerComparison = ({
  gauge,
}: {
  gauge: VisualisationDefinitions.Gauge;
}) => {
  const { client } = useContext(GqlClientContext);
  const { metricOptions } = useContext(MetricOptionsContext);
  const [data, setData] = useState<GaugeData | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const reportRequest = useReportGadgetRequest();
  const [args, setArgs] = useState<
    | {
        metrics: MetricInput[];
        expressions: Expression2[];
        dateScope: DateRangeInput;
        filterInput: FilterInput[];
        peerGroup: string;
        entity: string | undefined;
        peerGroupEntity?: FilterInput;
        client: ApolloClient<NormalizedCacheObject>;
      }
    | undefined
  >();
  const [formatting, setFormatting] = useState<MetricFormatting | undefined>();
  const { filterInput, lastDrilldown } = useFilterInput({
    extractEntityFilter: true,
  });
  const { datasets } = useContext(DatasetDefinitionsContext);
  const dateScope = useDateScope({});
  const toMetricInput = useToMetricInput();
  const { resolveMetricForRequest } = useMetricAggregationRequest();
  const usedMetric = metricOptions.find((m) => m.id === gauge.metricId);
  const isLowerBetter = isGaugeLowerBetter({ gauge, metric: usedMetric });

  const lastDrilldownTextFilter = useMemo(() => {
    if (!lastDrilldown) {
      return false;
    }

    const drilldownAsFixedFilters = fiToDrillDowns(lastDrilldown, datasets);
    return drilldownAsFixedFilters.find((df) =>
      filterTypeCheckers.isTextFilter(df),
    );
  }, [datasets, lastDrilldown]);

  const selectedPeerEntity = useMemo(() => {
    if (gauge.entity !== 'Dynamic' || !lastDrilldownTextFilter) {
      return undefined;
    }
    return lastDrilldown;
  }, [gauge.entity, lastDrilldown, lastDrilldownTextFilter]);

  // The percentile is ranked the gauge's way: reversed, lower is better
  // whatever the metric's own direction (higher is better unless told).
  // Always sent as a boolean: the server ranks a missing lowerIsBetter as
  // lower is better, which fills a higher-is-better gauge from the wrong end
  useEffect(() => {
    if (!usedMetric) {
      return;
    }

    if (metricTypeCheckers.isCompoundMetric(usedMetric)) {
      const resolved = resolveMetricForRequest(usedMetric);
      // Not ready → an input metric is still loading; don't fire a partial
      // request (backend "parse error: Expected EOF").
      if (!resolved.ready || !resolved.expression) {
        return;
      }

      setArgs({
        metrics: resolved.metricInputs,
        expressions: [
          {
            ...resolved.expression,
            lowerIsBetter: isLowerBetter,
          },
        ],
        filterInput: [filterInput],
        peerGroup: gauge.peerGroup,
        entity: selectedPeerEntity ? undefined : gauge.entity,
        peerGroupEntity: selectedPeerEntity,
        dateScope,
        client,
      });
    } else if (metricTypeCheckers.isNormalMetric(usedMetric)) {
      setArgs({
        metrics: [
          {
            ...toMetricInput(usedMetric),
            lowerIsBetter: isLowerBetter,
          },
        ],
        expressions: [
          {
            id: usedMetric.id,
            expression: usedMetric.id,
            lowerIsBetter: isLowerBetter,
          },
        ],
        filterInput: [filterInput],
        peerGroup: gauge.peerGroup,
        entity: selectedPeerEntity ? undefined : gauge.entity,
        peerGroupEntity: selectedPeerEntity,
        dateScope,
        client,
      });
    }
    setFormatting(usedMetric.formatting);
  }, [
    client,
    dateScope,
    filterInput,
    gauge.entity,
    gauge.peerGroup,
    isLowerBetter,
    resolveMetricForRequest,
    selectedPeerEntity,
    toMetricInput,
    usedMetric,
  ]);

  useNetworkingEffect(() => {
    if (args) {
      let isActive = true;
      setIsLoading(true);
      reportRequest('loading');
      retryOnce({ run: () => getPeerComparison(args) })
        .then((response) => {
          if (!isActive) {
            return;
          }
          const d = response.find((r) => r.id === gauge.metricId);
          if (d) {
            setData(d);
          }
          setIsLoading(false);
          reportRequest('done');
        })
        .catch((error) => {
          if (!isActive) {
            return;
          }
          // The gadget's boundary shows its error
          console.warn(error);
          reportRequest('failed');
        });

      return () => {
        isActive = false;
      };
    }
  }, [client, args, gauge.metricId, reportRequest]);

  return {
    isLoading,
    data,
    formatting,
    isLowerBetter,
  };
};

export default usePeerComparison;
